import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import Pagination from '../../components/Pagination';
import { DEFAULT_EXPLORE_URL } from '../../constants/explore';
import { desensitizeNumber, formatChg, getColor, getSizeUnit, getTokenLogoURL, loadDefaultImage, transformData } from '../../utils/exploreUtil';
import { useActiveWeb3React } from '../../hooks';
// Re-evaluate padding for better mobile spacing
const ExploreWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0px 12px; /* Uniform padding for mobile */

  @media only screen and (min-width: 960px) {
    padding: 0px 60px 0px 80px;
  }
`;

// Make TableView scrollable horizontally for overflowing content
const TableView = styled.div`
  width: 100%;
  overflow-x: auto; /* Enable horizontal scrolling */
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
`;

const TitleList = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 0px;
  /* Allow titles to wrap if too many on small screens */
  flex-wrap: wrap; 
`;

const Title = styled.span<{ active: boolean }>`
  display: flex;
  align-items: center;
  pointer-events: auto;
  justify-self: flex-start;
  margin: 0px 12px 0px 0px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme, active }) => (active ? theme.text1 : theme.text2)};
  border-bottom: 3px solid ${({ theme, active }) => (active ? '#014FFF' : 'transparent')};
  white-space: nowrap; /* Prevent title text from wrapping */

  :hover {
    color: ${({ theme }) => theme.text1};
  }

  @media only screen and (min-width: 960px) {
    font-size: 1rem;
  }
`;

const Rate = styled.div<{ rate: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 30px;
  border-radius: 5px;
  /* font-size: 0.75rem; */
  background-color: ${({ theme, rate }) => getColor(rate)};
  color: #ffffff;
`;

const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 0px;
  /* Ensure header width matches content for scrolling */
  min-width: fit-content; /* Allow content to dictate minimum width */
`;

const TableText = styled.div<{ hideOnMobile?: boolean; flexBasis?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 15px;
  color: ${({ theme }) => theme.text1};
  font-weight: 500;
  min-width: ${({ flexBasis }) => (flexBasis ? 'auto' : '100px')}; /* Adjust min-width for mobile */
  flex: ${({ flexBasis }) => flexBasis || '1'}; /* Use flexBasis for more control */

  /* Hide on mobile if specified, but ensure it doesn't break layout if not hidden */
  ${({ hideOnMobile }) =>
    hideOnMobile &&
    `
    @media only screen and (max-width: 959px) {
      display: none;
    }
  `}

  @media only screen and (min-width: 960px) {
    display: flex;
    font-size: 15px;
    min-width: 180px; /* Restore larger min-width for desktop */
  }
`;

const Icon = styled.img<{ size: number }>`
  width:${({ size}) => size}px;
  height:${({ size}) => size}px;
  margin-right: 4px;
  border-radius: ${({ size}) => size/2}px;
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 0px;
  min-width: fit-content; /* Ensure list item width matches content for scrolling */
  /* border-bottom: 1px solid ${({ theme }) => theme.bg3}; Add subtle separator */

  :hover {
    background-color: ${({ theme }) => theme.bg3};
    cursor: pointer;
  }
`;

export default function Explore({ history }: RouteComponentProps) {
  const { chainId } = useActiveWeb3React();
  const { t } = useTranslation();

  const [tabIndex, setTabIndex] = useState(1);
  const [list, setList] = useState<Array<any>>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize: number = 10;

  useEffect(() => {
    getPairByChain();
  }, [tabIndex, page, chainId]); // Add chainId to dependency array for re-fetch on chain change

  //获取列表
  const getPairByChain = () => {
    console.log('====================================');
    console.log('chainId:', chainId);
    console.log('====================================');

    let chain_id = chainId || 0; // Default to 0 if chainId is undefined
    let url = `${DEFAULT_EXPLORE_URL}/api/pairs?chain_id=${chain_id}&limit=${pageSize}&offset=${pageSize * (page - 1)}`;

    fetch(url, { method: 'GET' })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setList(data.data.data);
          setTotal(data.data.pagination.total);
        } else {
          // Handle API error, e.g., set empty list or show error message
          console.error('Failed to fetch data:', data);
          setList([]);
          setTotal(0);
        }
      })
      .catch((error) => {
        // Handle network error
        console.error('Network error:', error);
        setList([]);
        setTotal(0);
      });
  };

  const changeTab = (index: number) => {
    setTabIndex(index);
    setPage(1); // Reset page when tab changes
  };

  const goCoinInfo = (item: any) => {
    console.log(history);
    history.push(`/explore/${item.chain_id}/${item.pair_address}`);
  };

  return (
    <ExploreWrapper>
      <TitleList>
        <Title active={tabIndex === 1} onClick={() => changeTab(1)}>
          {t('pair')}
        </Title>
      </TitleList>
      <TableView>
        <TableHeader>
          <TableText hideOnMobile flexBasis="0.3" style={{ fontWeight: 'bold' }}>
            #
          </TableText>
          <TableText style={{ justifyContent: 'left', fontWeight: 'bold' }}>{t('tokenName')}</TableText>
          <TableText style={{ justifyContent: 'center', fontWeight: 'bold' }}>{t('price')}</TableText>
          {/* These columns will be hidden on mobile by default, but still occupy space on desktop */}
          <TableText hideOnMobile style={{ justifyContent: 'center', fontWeight: 'bold' }}>
            USD(24H)
          </TableText>
          <TableText hideOnMobile style={{ justifyContent: 'center', fontWeight: 'bold' }}>
            {t('tradeTimes')}(24H)
          </TableText>
          <TableText hideOnMobile style={{ justifyContent: 'center', fontWeight: 'bold' }}>
            {t('vol')}(24H)
          </TableText>
          <TableText style={{ justifyContent: 'right', fontWeight: 'bold', flexBasis: '0.5' }}>
            {t('oneDay')}
          </TableText>
        </TableHeader>
        {
          list.map((item, index) => (
            <ListItem key={index} onClick={() => goCoinInfo(item)}>
              <TableText hideOnMobile flexBasis="0.3">
                {index + 1 + (page - 1) * pageSize} {/* Corrected index for pagination */}
              </TableText>
              <TableText style={{ justifyContent: 'left', alignItems: 'center', fontWeight: 'bold' }}>
                <Icon size={25} src={getTokenLogoURL(item.chain_id, transformData(item).token0)} onError={loadDefaultImage}/>
                <Icon size={12} style={{ marginLeft: '-14px',marginTop: '13px' }} src={getTokenLogoURL(item.chain_id, transformData(item).token1)} onError={loadDefaultImage}/>
                {transformData(item).token0_symbol}/
                <div style={{fontWeight: 'normal'}}>{transformData(item).token1_symbol}</div>
              </TableText>
              <TableText style={{ justifyContent: 'center' }}>
                {/* {desensitizeNumber(item.price, 6)} {transformData(item).token1_symbol} */}
                ${desensitizeNumber(item.price_usd, 4)} 
              </TableText>
              <TableText hideOnMobile style={{ justifyContent: 'center' }}>
                {desensitizeNumber(item.volume_24h_usd, 4)}
              </TableText>
              <TableText hideOnMobile style={{ justifyContent: 'center' }}>
                {item.tx_count_24h}
              </TableText>
              <TableText hideOnMobile style={{ justifyContent: 'center' }}>
                {getSizeUnit(transformData(item).volume_24h_token0)}/{getSizeUnit(transformData(item).volume_24h_token1)}
              </TableText>
              <TableText style={{ justifyContent: 'right', flexBasis: '0.5' }}>
                <Rate rate={item.price_24h_change}>{formatChg(item.price_24h_change)}%</Rate>
              </TableText>
            </ListItem>
          ))
         }
      </TableView>
      <Pagination
        currentPage={page}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={(p: number) => {
          setPage(p);
        }}
      />
    </ExploreWrapper>
  );
}